import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { FileUploadDropzone } from "@/components/ui/file-upload-dropzone";
import { toast } from "sonner";
import { Loader2, FileText, Check, AlertTriangle } from "lucide-react";

interface Employee {
  id: string;
  name: string;
  employee_number?: string | null;
}

interface ExtractedBenefit {
  name: string;
  type: string;
  amount: number;
  frequency: string;
}

interface ExtractedBonus {
  name: string;
  type: string;
  amount: number;
  description?: string;
}

interface ExtractedSalaryData {
  employee_name: string | null;
  employee_number: string | null;
  period_month: string | null;
  base_salary: {
    amount: number;
    description: string;
  } | null;
  tax_class: string | null;
  health_insurance: {
    name: string | null;
    employee_share: number;
    employer_share: number;
  } | null;
  pension_contribution: {
    employee_share: number;
    employer_share: number;
  } | null;
  unemployment_insurance: {
    employee_share: number;
    employer_share: number;
  } | null;
  nursing_care_insurance: {
    employee_share: number;
    employer_share: number;
  } | null;
  benefits: ExtractedBenefit[];
  bonuses: ExtractedBonus[];
  deductions: { name: string; amount: number }[];
  gross_salary: number | null;
  net_salary: number | null;
  total_employer_cost: number | null;
}

export function DatevSalaryImport() {
  const { user } = useAuth();
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [extractedData, setExtractedData] = useState<ExtractedSalaryData | null>(null);
  const [matchedEmployee, setMatchedEmployee] = useState<Employee | null>(null);
  const [manualEmployeeId, setManualEmployeeId] = useState<string>("");

  useEffect(() => {
    if (user) {
      loadEmployees();
    }
  }, [user]);

  const loadEmployees = async () => {
    try {
      const { data, error } = await supabase
        .from("employees")
        .select("id, name, employee_number")
        .is("archived_at", null)
        .order("name");

      if (error) throw error;
      setEmployees(data || []);
    } catch (error) {
      console.error("Error loading employees:", error);
      toast.error("Fehler beim Laden der Mitarbeiter");
    } finally {
      setLoading(false);
    }
  };

  const findMatchingEmployee = useCallback((data: ExtractedSalaryData): Employee | null => {
    if (!data) return null;

    // Try to match by employee number first
    if (data.employee_number) {
      const match = employees.find(e => 
        e.employee_number?.toLowerCase() === data.employee_number?.toLowerCase()
      );
      if (match) return match;
    }

    // Try to match by name
    if (data.employee_name) {
      const normalizedName = data.employee_name.toLowerCase().trim();
      const match = employees.find(e => {
        const empName = e.name.toLowerCase().trim();
        return empName === normalizedName || 
               empName.includes(normalizedName) || 
               normalizedName.includes(empName);
      });
      if (match) return match;
    }

    return null;
  }, [employees]);

  const handleFileSelect = (file: File) => {
    setSelectedFile(file);
  };

  const handleClearFile = () => {
    setSelectedFile(null);
    setExtractedData(null);
    setMatchedEmployee(null);
    setManualEmployeeId("");
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      toast.error("Bitte wählen Sie eine Datei aus");
      return;
    }

    setUploading(true);
    setExtractedData(null);
    setMatchedEmployee(null);
    setManualEmployeeId("");

    try {
      const formData = new FormData();
      formData.append("file", selectedFile);

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        toast.error("Nicht angemeldet");
        return;
      }

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/parse-datev-salary`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${session.access_token}`,
          },
          body: formData,
        }
      );

      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(result.error || "Fehler beim Analysieren der Datei");
      }

      setExtractedData(result.data);
      const matched = findMatchingEmployee(result.data);
      setMatchedEmployee(matched);
      if (matched) {
        setManualEmployeeId(matched.id);
      }
      setDialogOpen(true);
      toast.success("Gehaltsabrechnung erfolgreich analysiert");
    } catch (error) {
      console.error("Error uploading file:", error);
      toast.error(error instanceof Error ? error.message : "Fehler beim Hochladen");
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    if (!extractedData || !manualEmployeeId) {
      toast.error("Bitte wählen Sie einen Mitarbeiter aus");
      return;
    }

    setSaving(true);
    try {
      // Save salary history entry
      if (extractedData.base_salary?.amount) {
        const { error: salaryError } = await supabase
          .from("salary_history")
          .insert([{
            employee_id: manualEmployeeId,
            effective_date: extractedData.period_month 
              ? `${extractedData.period_month}-01` 
              : new Date().toISOString().split('T')[0],
            base_salary: extractedData.base_salary.amount,
            currency: "EUR",
            salary_type: "monthly",
            payment_frequency: "monthly",
            tax_class: extractedData.tax_class,
            health_insurance: extractedData.health_insurance?.name,
            pension_contribution: extractedData.pension_contribution?.employee_share,
            reason: "datev_import",
            notes: `Importiert aus Datev-Abrechnung. Brutto: ${extractedData.gross_salary?.toLocaleString("de-DE", { style: "currency", currency: "EUR" }) || "-"}, Netto: ${extractedData.net_salary?.toLocaleString("de-DE", { style: "currency", currency: "EUR" }) || "-"}`,
            created_by: user!.id,
          }]);

        if (salaryError) throw salaryError;
      }

      // Save benefits
      if (extractedData.benefits && extractedData.benefits.length > 0) {
        const benefitsToInsert = extractedData.benefits
          .filter(b => b.amount > 0)
          .map(benefit => ({
            employee_id: manualEmployeeId,
            benefit_type: benefit.type || "other",
            benefit_name: benefit.name,
            start_date: extractedData.period_month 
              ? `${extractedData.period_month}-01` 
              : new Date().toISOString().split('T')[0],
            monthly_value: benefit.frequency === "yearly" 
              ? benefit.amount / 12 
              : benefit.amount,
            currency: "EUR",
            status: "active",
            payment_frequency: benefit.frequency === "yearly" ? "yearly" : "monthly",
            notes: `Importiert aus Datev-Abrechnung`,
            created_by: user!.id,
          }));

        if (benefitsToInsert.length > 0) {
          const { error: benefitsError } = await supabase
            .from("benefits_history")
            .insert(benefitsToInsert);

          if (benefitsError) throw benefitsError;
        }
      }

      // Save bonuses
      if (extractedData.bonuses && extractedData.bonuses.length > 0) {
        const bonusesToInsert = extractedData.bonuses
          .filter(b => b.amount > 0)
          .map(bonus => ({
            employee_id: manualEmployeeId,
            bonus_type: mapBonusType(bonus.type),
            bonus_date: extractedData.period_month 
              ? `${extractedData.period_month}-01` 
              : new Date().toISOString().split('T')[0],
            amount: bonus.amount,
            currency: "EUR",
            description: bonus.name,
            notes: bonus.description 
              ? `${bonus.description} - Importiert aus Datev-Abrechnung`
              : `Importiert aus Datev-Abrechnung`,
            created_by: user!.id,
          }));

        if (bonusesToInsert.length > 0) {
          const { error: bonusError } = await supabase
            .from("bonus_history")
            .insert(bonusesToInsert);

          if (bonusError) throw bonusError;
        }
      }

      toast.success("Daten erfolgreich importiert");
      setDialogOpen(false);
      setSelectedFile(null);
      setExtractedData(null);
    } catch (error) {
      console.error("Error saving data:", error);
      toast.error("Fehler beim Speichern der Daten");
    } finally {
      setSaving(false);
    }
  };

  const mapBonusType = (type: string): string => {
    const mapping: Record<string, string> = {
      'christmas_bonus': 'Weihnachtsgeld',
      'vacation_bonus': 'Urlaubsgeld',
      'performance_bonus': 'Leistungsprämie',
      'one_time': 'Sonderzahlung',
      'other': 'Sonstiger Bonus',
    };
    return mapping[type] || type;
  };

  const formatCurrency = (value: number | null | undefined) => {
    if (value === null || value === undefined) return "-";
    return value.toLocaleString("de-DE", { style: "currency", currency: "EUR" });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  const DATEV_IMPORT_FIELDS = [
    { label: "Mitarbeitername", required: false },
    { label: "Personalnummer", required: false },
    { label: "Abrechnungsmonat", required: true },
    { label: "Grundgehalt", required: true },
    { label: "Steuerklasse", required: false },
    { label: "Krankenversicherung", required: false },
    { label: "Rentenversicherung", required: false },
    { label: "Arbeitslosenversicherung", required: false },
    { label: "Pflegeversicherung", required: false },
    { label: "Benefits & Zulagen", required: false },
    { label: "Boni & Sonderzahlungen", required: false },
    { label: "Brutto-/Nettolohn", required: false },
    { label: "Arbeitgeberkosten", required: false },
  ];

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Datev Gehaltsabrechnung Import
          </CardTitle>
          <CardDescription>
            Laden Sie eine Gehaltsabrechnung von Datev Unternehmen Online hoch. 
            Die KI extrahiert automatisch Gehaltsdaten und Benefits.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <FileUploadDropzone
            accept=".pdf,image/*"
            onFileSelect={handleFileSelect}
            selectedFile={selectedFile}
            onClear={handleClearFile}
            title="Gehaltsabrechnung hochladen"
            description="Ziehen Sie eine PDF- oder Bilddatei hierher oder klicken Sie zum Auswählen"
            acceptedFormats={["PDF", "PNG", "JPG", "JPEG"]}
            supportedFields={DATEV_IMPORT_FIELDS}
            disabled={uploading}
          />

          {selectedFile && !uploading && (
            <div className="flex justify-center">
              <Button onClick={handleUpload}>
                <Loader2 className={`mr-2 h-4 w-4 ${uploading ? "animate-spin" : "hidden"}`} />
                Hochladen & Analysieren
              </Button>
            </div>
          )}

          {uploading && (
            <div className="flex items-center justify-center gap-2 py-4">
              <Loader2 className="h-5 w-5 animate-spin" />
              <span className="text-muted-foreground">KI analysiert die Gehaltsabrechnung...</span>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Extrahierte Gehaltsdaten</DialogTitle>
            <DialogDescription>
              Überprüfen Sie die extrahierten Daten und wählen Sie den Mitarbeiter aus
            </DialogDescription>
          </DialogHeader>

          {extractedData && (
            <div className="space-y-4">
              {/* Employee Matching */}
              <div className="p-4 rounded-lg border bg-muted/50">
                <div className="flex items-center gap-2 mb-3">
                  {matchedEmployee ? (
                    <Badge variant="default" className="bg-green-600">
                      <Check className="mr-1 h-3 w-3" />
                      Mitarbeiter gefunden
                    </Badge>
                  ) : (
                    <Badge variant="destructive">
                      <AlertTriangle className="mr-1 h-3 w-3" />
                      Kein Mitarbeiter gefunden
                    </Badge>
                  )}
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-muted-foreground">Aus Abrechnung</Label>
                    <p className="font-medium">{extractedData.employee_name || "-"}</p>
                    {extractedData.employee_number && (
                      <p className="text-sm text-muted-foreground">
                        Personalnr.: {extractedData.employee_number}
                      </p>
                    )}
                  </div>
                  <div>
                    <Label>Zuordnen zu</Label>
                    <Select 
                      value={manualEmployeeId} 
                      onValueChange={setManualEmployeeId}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Mitarbeiter wählen..." />
                      </SelectTrigger>
                      <SelectContent>
                        {employees.map((emp) => (
                          <SelectItem key={emp.id} value={emp.id}>
                            {emp.name} {emp.employee_number ? `(${emp.employee_number})` : ""}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* Salary Overview */}
              <div className="grid grid-cols-3 gap-4">
                <Card>
                  <CardContent className="pt-4">
                    <p className="text-sm text-muted-foreground">Grundgehalt</p>
                    <p className="text-xl font-bold">
                      {formatCurrency(extractedData.base_salary?.amount)}
                    </p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4">
                    <p className="text-sm text-muted-foreground">Brutto</p>
                    <p className="text-xl font-bold">
                      {formatCurrency(extractedData.gross_salary)}
                    </p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4">
                    <p className="text-sm text-muted-foreground">Netto</p>
                    <p className="text-xl font-bold">
                      {formatCurrency(extractedData.net_salary)}
                    </p>
                  </CardContent>
                </Card>
              </div>

              {/* Period & Tax */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-muted-foreground">Abrechnungsmonat</Label>
                  <p className="font-medium">{extractedData.period_month || "-"}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Steuerklasse</Label>
                  <p className="font-medium">{extractedData.tax_class || "-"}</p>
                </div>
              </div>

              {/* Social Insurance */}
              <div>
                <Label className="text-muted-foreground mb-2 block">Sozialversicherung</Label>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Versicherung</TableHead>
                      <TableHead className="text-right">AN-Anteil</TableHead>
                      <TableHead className="text-right">AG-Anteil</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    <TableRow>
                      <TableCell>
                        Krankenversicherung
                        {extractedData.health_insurance?.name && (
                          <span className="text-muted-foreground text-sm block">
                            {extractedData.health_insurance.name}
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        {formatCurrency(extractedData.health_insurance?.employee_share)}
                      </TableCell>
                      <TableCell className="text-right">
                        {formatCurrency(extractedData.health_insurance?.employer_share)}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Rentenversicherung</TableCell>
                      <TableCell className="text-right">
                        {formatCurrency(extractedData.pension_contribution?.employee_share)}
                      </TableCell>
                      <TableCell className="text-right">
                        {formatCurrency(extractedData.pension_contribution?.employer_share)}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Arbeitslosenversicherung</TableCell>
                      <TableCell className="text-right">
                        {formatCurrency(extractedData.unemployment_insurance?.employee_share)}
                      </TableCell>
                      <TableCell className="text-right">
                        {formatCurrency(extractedData.unemployment_insurance?.employer_share)}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Pflegeversicherung</TableCell>
                      <TableCell className="text-right">
                        {formatCurrency(extractedData.nursing_care_insurance?.employee_share)}
                      </TableCell>
                      <TableCell className="text-right">
                        {formatCurrency(extractedData.nursing_care_insurance?.employer_share)}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </div>

              {/* Benefits */}
              {extractedData.benefits && extractedData.benefits.length > 0 && (
                <div>
                  <Label className="text-muted-foreground mb-2 block">
                    Erkannte Benefits & Zulagen
                  </Label>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Bezeichnung</TableHead>
                        <TableHead>Typ</TableHead>
                        <TableHead className="text-right">Betrag</TableHead>
                        <TableHead>Frequenz</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {extractedData.benefits.map((benefit, idx) => (
                        <TableRow key={idx}>
                          <TableCell>{benefit.name}</TableCell>
                          <TableCell>
                            <Badge variant="outline">{benefit.type}</Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            {formatCurrency(benefit.amount)}
                          </TableCell>
                          <TableCell>{benefit.frequency}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}

              {/* Bonuses */}
              {extractedData.bonuses && extractedData.bonuses.length > 0 && (
                <div>
                  <Label className="text-muted-foreground mb-2 block">
                    Erkannte Boni & Sonderzahlungen
                  </Label>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Bezeichnung</TableHead>
                        <TableHead>Typ</TableHead>
                        <TableHead className="text-right">Betrag</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {extractedData.bonuses.map((bonus, idx) => (
                        <TableRow key={idx}>
                          <TableCell>
                            {bonus.name}
                            {bonus.description && (
                              <span className="text-muted-foreground text-sm block">
                                {bonus.description}
                              </span>
                            )}
                          </TableCell>
                          <TableCell>
                            <Badge variant="secondary">{mapBonusType(bonus.type)}</Badge>
                          </TableCell>
                          <TableCell className="text-right font-medium">
                            {formatCurrency(bonus.amount)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}

              {/* Total Employer Cost */}
              {extractedData.total_employer_cost && (
                <div className="p-4 rounded-lg border bg-primary/5">
                  <div className="flex justify-between items-center">
                    <span className="font-medium">Gesamte Arbeitgeberkosten</span>
                    <span className="text-xl font-bold">
                      {formatCurrency(extractedData.total_employer_cost)}
                    </span>
                  </div>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Abbrechen
            </Button>
            <Button 
              onClick={handleSave} 
              disabled={saving || !manualEmployeeId}
            >
              {saving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Speichern...
                </>
              ) : (
                <>
                  <Check className="mr-2 h-4 w-4" />
                  Daten übernehmen
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
